import { io } from 'socket.io-client';
export default class Socket {
  constructor(handlers = {}) {
    this.handlers = handlers;
    this.userInfo = null;
    this.socket = null;
    this.chatSocket = null;
    this.chatSessionId = null;
    this.evalSocket = null;
    this.evalSessionId = null;
    this.isConnected = false;
    this.isEvalConnected = false;
  }

  connect(userInfo) {
    if (this.isConnected && this.socket) {
      return this.socket;
    }

    this.userInfo = userInfo;

    const appName = process.env.VUE_APP_BACKEND_URL?.replace('https://', '').replace('http://', '');
    const baseUrl = process.env.VUE_APP_ENV === 'local' ? 'http://localhost:3000/notifications' : 'wss://' + appName + '/notifications';
    
    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 3,
      withCredentials: true,
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.joinRoom();
    });
  
    this.socket.on('disconnect', () => {
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
    });
  
    // Register event handlers
    this.socket.on('notification.create', (data) => {
      if (this.handlers.handleNewNotification) {
        this.handlers.handleNewNotification(data);
      }
    });
    
    this.socket.on('notification.update', (data) => {
      if (this.handlers.handleNotificationUpdated) {
        this.handlers.handleNotificationUpdated(data);
      }
    });
    
    this.socket.on('notification.delete', (data) => {
      if (this.handlers.handleNotificationDeleted) {
        this.handlers.handleNotificationDeleted(data);
      }
    });

    return this.socket;
  }

  connectChat(userInfo, chatSessionId) {
    if (this.isConnected && this.chatSocket) {
      return this.chatSocket;
    }

    this.userInfo = userInfo;
    this.chatSessionId = chatSessionId;

    const appName = process.env.VUE_APP_BACKEND_URL?.replace('https://', '').replace('http://', '');
    const baseUrl = process.env.VUE_APP_ENV === 'local' ? 'http://localhost:3000/chat' : 'wss://' + appName + '/chat';
    
    this.chatSocket = io(baseUrl, {
      auth: {
        sessionStorage: {
          chat_session_id: this.chatSessionId,
          userInfo: { ...this.userInfo },
        }
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 3,
      withCredentials: true,
    });

    this.chatSocket.on('connect', () => {
      this.isConnected = true;
      this.joinChatRoom();
      console.log('Chat WebSocket connected');
    });
  
    this.chatSocket.on('disconnect', () => {
      this.isConnected = false;
      this.leaveChatRoom();
      console.log('Chat WebSocket disconnected');
    });

    this.chatSocket.on('connection_error', (error) => {
      console.error('Chat WebSocket connection error:', error);
      this.isConnected = false;
      this.leaveChatRoom();
    });

    this.chatSocket.on('chat.error', (error) => {
      console.error('Chat WebSocket connection error:', error);
      this.isConnected = false;
    });
  
    // Register event handlers
    this.chatSocket.on('chat.response', (data) => {
      if (this.handlers.handleChatResponse) {
        this.handlers.handleChatResponse(data);
      }
    });

    return this.chatSocket;
  }

  connectEval(userInfo, evalSessionId) {
    if (this.isEvalConnected && this.evalSocket) {
      return this.evalSocket;
    }

    this.userInfo = userInfo;
    this.evalSessionId = evalSessionId;

    const appName = process.env.VUE_APP_BACKEND_URL?.replace('https://', '').replace('http://', '');
    const baseUrl = process.env.VUE_APP_ENV === 'local' ? 'http://localhost:3000/evals' : 'wss://' + appName + '/evals';
    
    this.evalSocket = io(baseUrl, {
      auth: {
        sessionStorage: {
          session_id: this.evalSessionId,
          userInfo: { ...this.userInfo },
        }
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 3,
      withCredentials: true,
    });

    this.evalSocket.on('connect', () => {
      this.isEvalConnected = true;
      this.joinEvalRoom();
    });
  
    this.evalSocket.on('disconnect', () => {
      this.isEvalConnected = false;
      this.leaveEvalRoom();
    });

    this.evalSocket.on('connection_error', (error) => {
      console.error('Eval Builder WebSocket connection error:', error);
      this.isEvalConnected = false;
      this.leaveEvalRoom();
    });

    this.evalSocket.on('eval.error', (error) => {
      console.error('Eval Builder WebSocket error:', error);
      this.isEvalConnected = false;
    });
    
    // Register event handler for eval messages
    this.evalSocket.on('eval_message_received', (data) => {
      if (this.handlers.handleEvalMessage) {
        this.handlers.handleEvalMessage(data);
      }
    });
  
    // Register event handlers
    this.evalSocket.on('eval:preview:created', (data) => {
      if (this.handlers.handleEvalPreviewCreated) {
        this.handlers.handleEvalPreviewCreated(data);
      }
    });

    this.evalSocket.on('eval:preview:status', (data) => {
      if (this.handlers.handleEvalPreviewStatusUpdated) {
        this.handlers.handleEvalPreviewStatusUpdated(data);
      }
    });

    return this.evalSocket;
  }

  joinRoom() {
    if (!this.userInfo || !this.socket) {
      console.warn('Cannot join room: missing userInfo or socket connection');
      return;
    }
    
    const userRoom = `user_${this.userInfo.id}`;
    this.socket.emit('join', userRoom);
  }

  leaveRoom() {
    if (!this.userInfo || !this.socket) return;
    
    const userRoom = `user_${this.userInfo.id}`;
    this.socket.emit('leave', userRoom);
  }

  joinChatRoom() {
    if (!this.userInfo || !this.chatSocket) {
      console.warn('Cannot join room: missing userInfo or chat socket connection');
      return;
    }
    
    const userRoom = `chat_user_${this.userInfo.id}`;
    this.chatSocket.emit('join', userRoom);
  }

  leaveChatRoom() {
    if (!this.userInfo || !this.chatSocket) return;
    
    const userRoom = `chat_user_${this.userInfo.id}`;
    this.chatSocket.emit('leave', userRoom);
  }

  joinEvalRoom() {
    if (!this.userInfo || !this.evalSocket) {
      console.warn('Cannot join room: missing userInfo or eval socket connection');
      return;
    }

    const userRoom = `eval_user_${this.userInfo.id}`;
    this.evalSocket.emit('join', userRoom);
  }

  leaveEvalRoom() {
    if (!this.userInfo || !this.evalSocket) return;
    
    const userRoom = `eval_user_${this.userInfo.id}`;
    this.evalSocket.emit('leave', userRoom);
  }

  sendEvalPreviewCreated(data) {
    if (!this.evalSocket || !this.isEvalConnected) {
      console.warn('Cannot send eval preview created: eval socket not connected');
      return;
    }
    const payload = {...data}
    this.evalSocket.emit('eval:preview:created', payload);
  }

  sendEvalPreviewStatusUpdated(data) {
    if (!this.evalSocket || !this.isEvalConnected) {
      console.warn('Cannot send eval preview status updated: eval socket not connected');
      return;
    }
    const payload = {...data}
    this.evalSocket.emit('eval:preview:status', payload);
  }

  disconnectEval() {
    if (this.evalSocket) {
      this.evalSocket.disconnect();
      this.evalSocket = null;
      this.isEvalConnected = false;
      console.log('Eval Builder WebSocket disconnected');
    }
  }

  sendMessage(message) {
    if (!this.chatSocket || !this.isConnected) {
      console.warn('Cannot send message: chat socket not connected');
      return;
    }
    
    this.chatSocket.emit('chat.message', message);
  }

  sendTaskBundlesDownloaded(datasetId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot send task bundles downloaded: socket not connected');
      return;
    }
    
    this.socket.emit('task_bundles_downloaded', datasetId);
  }

  isChatSocketConnected() {
    return this.isConnected && this.chatSocket;
  }

  disconnectChat() {
    if (this.chatSocket) {
      this.chatSocket.disconnect();
      this.chatSocket = null;
      this.isConnected = false;
      console.log('Chat WebSocket disconnected');
    }
  }

  disconnect() {
    if (this.socket) {
      this.leaveRoom();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
    this.disconnectChat();
    this.disconnectEval();
  }

  updateHandlers(handlers) {
    this.handlers = { ...this.handlers, ...handlers };
  }
}
